<template>
    <div class="container-fluid">
        <!-- LOADING -->
        <Loading
            :style="{
                display: (!user.safe_user || loaded.drugSafeUser) && loaded.webUser ? 'none' : 'block'
            }"
        />
        <!-- LOADED -->
        <div
            :style="{
                display: (!user.safe_user || loaded.drugSafeUser) && loaded.webUser ? 'initial' : 'none'
            }"
        >
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>{{ $t('update_account') }}</h3>
            </div>
            <!-- TABS BAR -->
            <div class="d-flex flex-row justify-content-between mt-5 mb-5" v-if="user.safe_user">
                <div class="d-flex flex-column" :style="{ width: '49%' }">
                    <button :class="tabs.webUser ? 'btn btn-dark' : 'btn btn-light'" v-on:click="switchTab('webUser')">
                        {{ $t('web_user') }}
                    </button>
                </div>
                <div class="d-flex flex-column" :style="{ width: '49%' }">
                    <button
                        :class="tabs.drugSafeUser ? 'btn btn-dark' : 'btn btn-light'"
                        v-on:click="switchTab('drugSafeUser')"
                    >
                        {{ $t('drugsafe_user') }}
                    </button>
                </div>
            </div>
            <!-- TAB: EDIT WEB USER -->
            <div class="row mt-4" :style="{ display: tabs.webUser ? 'block' : 'none' }">
                <div class="col-sm-8 col-md-6 col-lg-5 col-xl-4">
                    <ValidationObserver ref="editWebUserForm" v-slot="{ handleSubmit, invalid }">
                        <form role="form" @submit.prevent="handleSubmit(handleWebUserFormSubmitClick)">
                            <!-- FORM FIELDS -->
                            <WebUserFormFields :edit="true" @loaded="setLoaded('webUser')" :webUser="user"  />
                            <!-- BUTTONS -->
                            <div class="d-flex flex-row mt-4">
                                <div class="d-flex flex-column mr-3">
                                    <input
                                        type="submit"
                                        class="btn btn-success"
                                        :value="$t('save')"
                                        :disabled="submitClicked || invalid"
                                    />
                                </div>
                                <div class="d-flex flex-column">
                                    <button
                                        type="button"
                                        class="btn btn-md btn-secondary"
                                        v-on:click="$router.go(-1)"
                                        :disabled="submitClicked"
                                    >
                                        {{ $t('cancel') }}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </ValidationObserver>
                </div>
            </div>
            <!-- TAB: EDIT DRUGSAFE USER -->
            <div
                class="row mt-4"
                v-if="!user.permissions.superadmin && Object.keys(drugSafeUser).length > 2"
                :style="{ display: tabs.drugSafeUser ? 'block' : 'none' }"
            >
                <div class="col-sm-8 col-md-6 col-lg-5 col-xl-4">
                    <ValidationObserver ref="editDrugSafeUserForm" v-slot="{ handleSubmit, invalid }">
                        <form role="form" @submit.prevent="handleSubmit(submitDrugSafeUserForm)">
                            <!-- FORM FIELDS -->
                            <DrugSafeUserFormFields
                                :createWebUser="false"
                                :edit="true"
                                @loaded="setLoaded('drugSafeUser')"
                                :drugSafeUser="drugSafeUser"
                                :pharmacies="pharmacies"
                            />
                            <!-- BUTTONS -->
                            <div class="d-flex flex-row mt-4">
                                <div class="d-flex flex-column mr-3">
                                    <input
                                        type="submit"
                                        class="btn btn-success"
                                        :value="$t('save')"
                                        :disabled="submitClicked || invalid"
                                    />
                                </div>
                                <div class="d-flex flex-column">
                                    <button
                                        type="button"
                                        class="btn btn-md btn-secondary"
                                        v-on:click="$router.go(-1)"
                                        :disabled="submitClicked"
                                    >
                                        {{ $t('cancel') }}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </ValidationObserver>
                </div>
            </div>
            <!-- MODAL: ARE YOU SURE YOU WANT TO INACTIVATE WEB USER? -->
            <b-modal
                id="submitWebUserFormConfirmationModal"
                centered
                :title="$t('inactivate_own_web_user_are_you_sure_title')"
                hide-footer
            >
                <div class="row">
                    <div class="col">
                        <p>{{ $t('inactivate_own_web_user_are_you_sure') }}</p>
                    </div>
                </div>
                <div class="d-flex row mt-2">
                    <div class="d-flex col justify-content-center">
                        <button class="btn btn-primary mr-4" @click="submitWebUserForm">
                            {{ $t('ok') }}
                        </button>
                        <button class="btn btn-secondary" @click="handleWebUserFormConfirmationCancel">
                            {{ $t('cancel') }}
                        </button>
                    </div>
                </div>
            </b-modal>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Loading from '@/views/partial/Loading';
import { getDrugSafeUser, handleAxiosError, updateDrugSafeUser, updateWebUser } from '@/services/axiosService';
import DrugSafeUserFormFields from './drugSafe/DrugSafeUserFormFields';
import WebUserFormFields from './web/WebUserFormFields';

export default {
    components: {
        Loading,
        DrugSafeUserFormFields,
        WebUserFormFields
    },
    data() {
        return {
            drugSafeUser: {
                pincode: null,
                pincode_confirmation: null
            },
            loaded: {
                drugSafeUser: false,
                webUser: false
            },
            pharmacies: [],
            submitClicked: false,
            tabs: {
                webUser: true,
                drugSafeUser: false
            }
        };
    },
    async created() {
        // if current user is not superadmin and is associated with drugsafe user, get drugsafe user
        if (!this.user.permissions.superadmin && this.user.safe_user) {
            await this.getDrugSafeUser();
        }
    },
    computed: {
        ...mapState('account', ['user'])
    },
    methods: {
        ...mapActions('account', ['logout', 'updateProfile']),
        // exit page
        async exit() {
            this.$toastr.s(this.$t('account_updated'));
            if (this.user.status === 'inactive') {
                // if user has set themselves to inactive, log out user
                this.logout();
            } else {
                // otherwise update user profile based on changes and return to previous page
                await this.updateProfile();
                this.$router.back();
            }
        },
        // get drugsafe user
        async getDrugSafeUser() {
            try {
                const drugSafeUser = await getDrugSafeUser(this.user.pharmacy_id, this.user.safe_user.id);
                this.drugSafeUser = { ...this.drugSafeUser, ...drugSafeUser };
            } catch (error) {
                handleAxiosError(error);
            }
        },
        // handle web user form submit cancel (via modal)
        handleWebUserFormConfirmationCancel() {
            this.submitClicked = false;
            this.$bvModal.hide('submitWebUserFormConfirmationModal');
        },
        // handle web user form submit click
        async handleWebUserFormSubmitClick() {
            this.submitClicked = true;
            if (this.user.status === 'inactive') {
                // if user is inactivating themselves, show confirmation modal
                this.$bvModal.show('submitWebUserFormConfirmationModal');
            } else {
                // otherwise submit form directly
                await this.submitWebUserForm();
            }
        },
        // set page loaded
        setLoaded(component) {
            this.loaded[component] = true;
        },
        // submit drugsafe user form and exit page
        async submitDrugSafeUserForm() {
            try {
                this.submitClicked = true;
                delete this.drugSafeUser.web_user_object;
                delete this.drugSafeUser.web_user_object_original;
                await updateDrugSafeUser(this.user.pharmacy_id, this.drugSafeUser.id, this.drugSafeUser);
                await this.exit();
            } catch (error) {
                handleAxiosError(error);
                // if backend returns 409 (= pin not unique), show error in form
                if (error.response.data.status === 409) {
                    this.$refs.editDrugSafeUserForm.setErrors({ drugSafeUserPin: ['error_pin_not_unique'] });
                }
                this.submitClicked = false;
            }
        },
        // submit web user form and exit page
        async submitWebUserForm() {
            try {
                this.$bvModal.hide('submitWebUserFormConfirmationModal');
                await updateWebUser(this.user.id, this.user);
                await this.exit();
            } catch (error) {
                handleAxiosError(error);
                // if backend returns 409 (= e-mail address already taken), show error in form
                if (error.response.data.status === 409) {
                    this.$refs.editWebUserForm.setErrors({ webUserEmail: ['error_email_taken'] });
                }
                this.submitClicked = false;
            }
        },
        // switch tab
        switchTab(tabName) {
            for (let tab in this.tabs) {
                this.tabs[tab] = tab == tabName;
            }
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.submitClicked = false;
            }
        });
    }
};
</script>
